import { useEffect, useRef, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";

/**
 * Search and filters, without the stack of dropdowns.
 *
 * On a phone the previous version wrapped a search box and three selects onto
 * four separate lines and pushed the table most of a screen down, which is a lot
 * of chrome to look at while trying to find one process.
 *
 * So: the search box keeps the line to itself, the filters go behind one button
 * badged with how many are on, and whatever is active comes back as a removable
 * chip. Nothing is hidden — the state is visible in the chips — it just is not
 * all expanded at once. On a desktop, where there is room, the selects sit
 * inline as before.
 */
/**
 * `toggles` are view modes rather than filters: they change how the same rows are
 * arranged, not which ones survive. They sit beside the filters but are
 * deliberately not counted as active ones, and "clear filters" leaves them alone —
 * grouping a list is not a thing you get stuck in.
 */
export function FilterBar({ query, onQuery, placeholder = "Search", filters = [], toggles = [], count }) {
    const [open, setOpen] = useState(false);
    const panel = useRef(null);

    const active = filters.filter((filter) => filter.value);

    useEffect(() => {
        if (!open) return undefined;
        const onAway = (event) => !panel.current?.contains(event.target) && setOpen(false);
        const onKey = (event) => event.key === "Escape" && setOpen(false);
        document.addEventListener("pointerdown", onAway);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onAway);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const clearAll = () => {
        onQuery("");
        for (const filter of filters) filter.onChange("");
    };

    const labelFor = (filter) => filter.options.find((option) => option.value === filter.value)?.label ?? filter.value;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1 lg:max-w-xs">
                    <Search size={15} className="muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
                    <input
                        className="input pl-8"
                        placeholder={placeholder}
                        value={query}
                        onChange={(event) => onQuery(event.target.value)}
                        aria-label={placeholder}
                        // What the global "/" shortcut focuses.
                        data-page-search=""
                    />
                </div>

                {/* Inline on a desktop, behind a button on a phone. */}
                <div className="hidden items-center gap-2 lg:flex">
                    {toggles.map((toggle) => (
                        <button
                            key={toggle.key}
                            onClick={() => toggle.onChange(!toggle.value)}
                            aria-pressed={toggle.value}
                            title={toggle.label}
                            className={`btn-ghost h-[38px] shrink-0 ${
                                toggle.value ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300" : ""
                            }`}
                        >
                            {toggle.icon ? <toggle.icon size={14} aria-hidden /> : null}
                            {toggle.label}
                        </button>
                    ))}
                    {filters.map((filter) => (
                        <select
                            key={filter.key}
                            className="input w-auto min-w-28"
                            value={filter.value}
                            onChange={(event) => filter.onChange(event.target.value)}
                            aria-label={filter.label}
                        >
                            <option value="">{filter.label}</option>
                            {filter.options.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    ))}
                </div>

                {filters.length + toggles.length > 0 ? (
                    <div className="relative lg:hidden" ref={panel}>
                        <button
                            onClick={() => setOpen((value) => !value)}
                            aria-expanded={open}
                            aria-label="Filters"
                            className={`btn-ghost h-[38px] shrink-0 ${active.length ? "border-blue-500 text-blue-600 dark:border-blue-500 dark:text-blue-400" : ""}`}
                        >
                            <SlidersHorizontal size={15} />
                            {active.length ? <span className="tabular-nums">{active.length}</span> : null}
                        </button>

                        {open ? (
                            <div className="card absolute right-0 top-11 z-20 w-64 space-y-3 p-3 shadow-lg">
                                {toggles.map((toggle) => (
                                    <label key={toggle.key} className="flex cursor-pointer items-center justify-between gap-3 text-sm">
                                        <span className="flex items-center gap-2">
                                            {toggle.icon ? <toggle.icon size={14} className="muted" aria-hidden /> : null}
                                            {toggle.label}
                                        </span>
                                        <input
                                            type="checkbox"
                                            className="size-4 shrink-0"
                                            checked={toggle.value}
                                            onChange={(event) => toggle.onChange(event.target.checked)}
                                        />
                                    </label>
                                ))}
                                {filters.map((filter) => (
                                    <div key={filter.key}>
                                        <label className="label mb-1" htmlFor={`filter-${filter.key}`}>{filter.label}</label>
                                        <select
                                            id={`filter-${filter.key}`}
                                            className="input"
                                            value={filter.value}
                                            onChange={(event) => filter.onChange(event.target.value)}
                                        >
                                            <option value="">Any</option>
                                            {filter.options.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {count ? <span className="muted ml-auto hidden shrink-0 text-xs tabular-nums sm:block">{count}</span> : null}
            </div>

            {/* What is on, and how to turn it off — visible without opening anything. */}
            {active.length > 0 || query ? (
                <div className="flex flex-wrap items-center gap-1.5">
                    {query ? (
                        <Chip label={`“${query}”`} onRemove={() => onQuery("")} />
                    ) : null}
                    {active.map((filter) => (
                        <Chip key={filter.key} label={labelFor(filter)} onRemove={() => filter.onChange("")} />
                    ))}
                    <button className="link ml-1 text-xs" onClick={clearAll}>Clear all</button>
                    {count ? <span className="muted ml-auto text-xs tabular-nums sm:hidden">{count}</span> : null}
                </div>
            ) : null}
        </div>
    );
}

function Chip({ label, onRemove }) {
    return (
        <span className="badge-queued gap-1 pr-1">
            <span className="max-w-40 truncate">{label}</span>
            <button onClick={onRemove} aria-label={`Remove ${label}`} className="rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700">
                <X size={11} />
            </button>
        </span>
    );
}
